import { useLazyQuery } from '@apollo/client'
import { useAuth0 } from '@auth0/auth0-react'
import { useLocation } from '@gatsbyjs/reach-router'
import queryString from 'query-string'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import { LibraryResourcesDocument, LibraryResourcesQuery } from '../graphql/search'
import { useSessionAuth } from '../lib/auth/hooks/useSessionAuth'
import { ApolloClientsContext } from '../lib/graphql/apollo'
import { usePageUrl } from '../lib/pages'
import {
  defaultState as defaultSearchOptions,
  resourceCategoryUrlMapping,
} from '../utils/libraryTools'
import { isUserCategory, userCategories } from '../utils/userClaims'
import useUserCategory from './useUserCategory'

type ResourceCategoryUrlKey = keyof typeof resourceCategoryUrlMapping

type SearchOptions = {
  first: number
  types: string[]
  query: string | null
  after: string | null
  libraryResourceFilters: Array<{ category: string | null }>
  orderBy: Array<{ direction: 'ASC' | 'DESC'; field: string }>
}

type Resource = {
  _id: string
  slug: { current: string }
  title: string
  category: string
  categorySlug: string
  thumbnailImage: { json: string }
}

type Meta = {
  count: Record<string, number>
}

type TransformedData = {
  meta: Meta
  resources: Resource[]
}

function parseQueryParamsIntoSearchOptions(
  defaultState: typeof defaultSearchOptions,
  { query, type, sort }: { query?: string; type?: string; sort?: string },
): SearchOptions {
  const category =
    type && type in resourceCategoryUrlMapping
      ? resourceCategoryUrlMapping[type as ResourceCategoryUrlKey]
      : null

  return {
    ...defaultState,
    query: query || null,
    libraryResourceFilters: [
      {
        category,
      },
    ],
    orderBy: [
      {
        direction: sort === 'desc' ? 'DESC' : 'ASC',
        field: 'NAME',
      },
    ],
  }
}

const initialState = { ...defaultSearchOptions }

function transformSearchResultsData(
  data: LibraryResourcesQuery | undefined,
  searchOptions: SearchOptions,
  initialCountsRef: React.MutableRefObject<Record<string, number> | null>,
): TransformedData {
  if (!data?.search?.nodes) {
    return {
      meta: {
        count: {
          all: 0,
        },
      },
      resources: [],
    }
  }

  const libraryResources = data.search.nodes
  const categoryCounts =
    data.search.filters?.libraryResource?.category?.options?.reduce(
      (acc: Record<string, number>, option: { count: number; value: string }) => {
        acc[option.value] = option.count
        return acc
      },
      {},
    ) || {}

  // Store initial counts when no filter is active
  if (!searchOptions.libraryResourceFilters[0].category) {
    initialCountsRef.current = categoryCounts
  }

  function isLibraryResource(
    node: LibraryResourcesQuery['search']['nodes'][number],
  ): node is Extract<
    LibraryResourcesQuery['search']['nodes'][number],
    { __typename?: 'LibraryResource' }
  > {
    return node.__typename === 'LibraryResource'
  }

  return {
    meta: {
      count: {
        all: data.search.totalCount,
        ...(searchOptions.libraryResourceFilters[0].category
          ? categoryCounts
          : initialCountsRef.current),
      },
    },
    resources: libraryResources.filter(isLibraryResource).map((node) => ({
      _id: node.id,
      slug: { current: node.slug },
      title: node.title,
      category: node.category,
      categorySlug: node.categorySlug,
      thumbnailImage: node.thumbnailImage || { json: '' },
    })),
  }
}

const useResourceLibrarySearch = (query?: string) => {
  useSessionAuth()
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0()
  const { comma: commaClient } = useContext(ApolloClientsContext)

  const location = useLocation()
  const { navigate } = usePageUrl()
  const initialCountsRef = useRef<Record<string, number> | null>(null)

  const [executeSearch, { data, loading, error, fetchMore }] = useLazyQuery<LibraryResourcesQuery>(
    LibraryResourcesDocument,
    {
      client: commaClient,
      context: {
        useLatestTokenIfLoggedIn: true,
        uri: process.env.GATSBY_SEARCH_URL,
      },
    },
  )

  const searchOptions: SearchOptions = useMemo(() => {
    const queryParams = queryString.parse(location.search || '')
    return {
      ...parseQueryParamsIntoSearchOptions(initialState, { ...queryParams, query }),
    }
  }, [location.search, query])

  const getQueryVariables = useCallback(
    () => ({
      query: searchOptions.query,
      first: searchOptions.first,
      types: searchOptions.types,
      libraryResourceFilters: searchOptions.libraryResourceFilters,
      orderBy: searchOptions.orderBy,
    }),
    [searchOptions],
  )

  const { userCategory } = useUserCategory()
  const isProfessional =
    isUserCategory(userCategory) &&
    (userCategory === userCategories.employee ||
      userCategory === userCategories.practitioner ||
      userCategory === userCategories.otherProfessional)

  useEffect(() => {
    executeSearch({
      variables: getQueryVariables(),
    })
  }, [
    isAuthenticated,
    isLoading,
    isProfessional,
    location,
    loginWithRedirect,
    navigate,
    searchOptions,
    executeSearch,
    getQueryVariables,
  ])

  const fetchNextSlice = useCallback(async () => {
    if (!fetchMore || !data?.search?.pageInfo?.hasNextPage) {
      return
    }

    return fetchMore({
      variables: {
        ...getQueryVariables(),
        after: data.search.pageInfo.endCursor,
      },
      updateQuery: (
        prev: LibraryResourcesQuery,
        { fetchMoreResult }: { fetchMoreResult: LibraryResourcesQuery | undefined },
      ) => {
        if (!fetchMoreResult) return prev

        return {
          ...fetchMoreResult,
          search: {
            ...fetchMoreResult.search,
            nodes: [...prev.search.nodes, ...fetchMoreResult.search.nodes],
            pageInfo: fetchMoreResult.search.pageInfo,
          },
        }
      },
    })
  }, [fetchMore, data?.search?.pageInfo, getQueryVariables])

  const transformedData = transformSearchResultsData(data, searchOptions, initialCountsRef)

  return {
    data: transformedData,
    loading,
    error,
    isProfessional,
    searchOptions,
    actions: {
      fetchNextSlice,
    },
    hasNextPage: data?.search?.pageInfo?.hasNextPage,
  }
}

export default useResourceLibrarySearch
